import { CSSProperties, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  IconButton,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {
  addHours,
  addMinutes,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  getDay,
  parse,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { enUS, ar, ca, de, es, fr, it, nb, ro, lt } from "date-fns/locale";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useCookies } from "react-cookie";
import { Autocomplete } from "@mui/material";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SchedulingService from "../../services/SchedulingService";
import {
  CareTeamAppointment,
  UserTimeOffBlock,
  UserWorkingHour,
} from "../../interfaces/Scheduling";
import { UserContext } from "../../context/UserContext";
import { CCMPatient } from "../../interfaces/CCMPatient";
import PatientService from "../../services/PatientService";
import { User } from "../../interfaces/user/User";

const locales = {
  "en": enUS,
  "ar": ar,
  "ca": ca,
  "de": de,
  "es": es,
  "fr": fr,
  "it": it,
  "no": nb,
  "ro": ro,
  "lt": lt,
};

const localizer = dateFnsLocalizer({
  format: (date: Date, formatStr: string, options?: { locale?: Locale }) => 
    format(date, formatStr, { ...options }),
  parse,
  startOfWeek: (date: Date) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales,
});

const getWeekdays = (t: (key: string) => string) => [
  { label: t("monday"), value: 0 },
  { label: t("tuesday"), value: 1 },
  { label: t("wednesday"), value: 2 },
  { label: t("thursday"), value: 3 },
  { label: t("friday"), value: 4 },
  { label: t("saturday"), value: 5 },
  { label: t("sunday"), value: 6 },
];

const WEEKEND_DAYS = new Set([5, 6]);

const getWeekdayIndexFromDate = (date: Date) => {
  // Convert JS getDay (Sunday = 0) to our Monday=0 convention
  return (date.getDay() + 6) % 7;
};

type CalendarEvent = {
  id: string;
  title: string;
  start: Date;
  end: Date;
  status: string;
  resource: CareTeamAppointment;
};

const DEFAULT_DURATION = 30;
type CalendarView = "day" | "week" | "month" | "agenda";
const DEFAULT_VIEW: CalendarView = "week";
const AVAILABLE_VIEWS: CalendarView[] = ["day", "week", "month"];

const parseTimeToMinutes = (time: string) => {
  const [hours = "0", minutes = "0"] = time.split(":");
  return parseInt(hours, 10) * 60 + parseInt(minutes, 10);
};

const getMinutesFromDate = (date: Date) => date.getHours() * 60 + date.getMinutes();

const minutesToDayDate = (minutes: number) => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setMinutes(minutes);
  return date;
};

const getReadableError = (error: any): string | null => {
  if (!error) return null;
  const source = typeof error === "string" ? error : error.message || error;
  if (typeof source !== "string") {
    if (typeof source === "object" && source !== null) {
      if ("error" in source && typeof source.error === "string") {
        return source.error;
      }
      if ("detail" in source && typeof source.detail === "string") {
        return source.detail;
      }
      return JSON.stringify(source);
    }
    return null;
  }
  try {
    const parsed = JSON.parse(source);
    if (typeof parsed === "string") {
      return parsed;
    }
    if (parsed && typeof parsed === "object") {
      if ("error" in parsed && typeof parsed.error === "string") {
        return parsed.error;
      }
      if ("detail" in parsed && typeof parsed.detail === "string") {
        return parsed.detail;
      }
      return JSON.stringify(parsed);
    }
  } catch (_) {
    // source was plain text
    return source;
  }
  return source;
};

const getViewRange = (date: Date, view: CalendarView) => {
  switch (view) {
    case "day":
      return { start: startOfDay(date), end: endOfDay(date) };
    case "month":
      return {
        start: startOfWeek(startOfMonth(date), { weekStartsOn: 1 }),
        end: endOfWeek(endOfMonth(date), { weekStartsOn: 1 }),
      };
    case "agenda":
    case "week":
    default:
      return {
        start: startOfWeek(date, { weekStartsOn: 1 }),
        end: endOfWeek(date, { weekStartsOn: 1 }),
      };
  }
};

const timeStringToDate = (time: string) => {
  const [hours, minutes, seconds] = time.split(":").map((val) => parseInt(val, 10));
  const date = new Date();
  date.setHours(hours || 0, minutes || 0, seconds || 0, 0);
  return date;
};

const formatTime = (date: Date | null) => {
  if (!date) return "00:00:00";
  return format(date, "HH:mm:ss");
};

const roleCanManageSchedule = (user: User | null | undefined) => {
  const role = user?.role?.roletype || user?.roletype || "";
  return ["orgadmin", "staffuser", "physician"].includes(role.toLowerCase());
};

const getCalendarMessages = (t: (key: string) => string) => ({
  today: t("today"),
  back: t("back"),
  next: t("next"),
  previous: t("previous"),
  month: t("month"),
  week: t("week"),
  day: t("day"),
  agenda: t("agenda"),
  date: t("date"),
  time: t("time"),
  event: t("event"),
  allDay: t("all_day"),
  yesterday: t("yesterday"),
  tomorrow: t("tomorrow"),
  noEventsInRange: t("no_events_in_range"),
  showMore: (total: number) => `+${total} ${t("more")}`,
});

export const MySchedule = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const userCtx = useContext(UserContext);
  const user = userCtx?.user;
  const [cookies] = useCookies(["access_token"]);
  const token = cookies.access_token;
  const navigate = useNavigate();
  const WEEKDAYS = useMemo(() => getWeekdays(t), [t]);

  const [workingHours, setWorkingHours] = useState<UserWorkingHour[]>([]);
  const [loadingWorkingHours, setLoadingWorkingHours] = useState(false);
  const [initialHoursChecked, setInitialHoursChecked] = useState(false);
  const [showDefaultHoursDialog, setShowDefaultHoursDialog] = useState(false);
  const [settingDefaultHours, setSettingDefaultHours] = useState(false);
  const [missingWeekdays, setMissingWeekdays] = useState<number[]>([]);
  const [workingHoursListOpen, setWorkingHoursListOpen] = useState(false);
  const [timeOffListOpen, setTimeOffListOpen] = useState(false);

  const [timeOffBlocks, setTimeOffBlocks] = useState<UserTimeOffBlock[]>([]);
  const [loadingTimeOff, setLoadingTimeOff] = useState(false);

  const [appointments, setAppointments] = useState<CareTeamAppointment[]>([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);

  const [calendarView, setCalendarView] = useState<CalendarView>(DEFAULT_VIEW);
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());
  const [calendarRange, setCalendarRange] = useState(() => getViewRange(new Date(), DEFAULT_VIEW));

  const [patients, setPatients] = useState<CCMPatient[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  const [workingHourDialogOpen, setWorkingHourDialogOpen] = useState(false);
  const [workingHourDraft, setWorkingHourDraft] = useState<UserWorkingHour>({
    weekday: 0,
    start_time: "09:00:00",
    end_time: "17:00:00",
    is_active: true,
  });

  const [timeOffDialogOpen, setTimeOffDialogOpen] = useState(false);
  const [timeOffDraft, setTimeOffDraft] = useState<{
    id?: number;
    start: Date;
    end: Date;
    reason?: string;
  }>({
    start: new Date(),
    end: addHours(new Date(), 1),
  });

  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [appointmentDraft, setAppointmentDraft] = useState<{
    patientId: string;
    start: Date;
    durationMinutes: number;
    notes: string;
  }>(() => {
    // Set default time to next hour with minutes = 00
    const now = new Date();
    const nextHour = new Date(now);
    nextHour.setHours(now.getHours() + 1, 0, 0, 0); // Next hour, 00 minutes
    return {
      patientId: "",
      start: nextHour,
      durationMinutes: DEFAULT_DURATION,
      notes: "",
    };
  });
  const todayStart = useMemo(() => startOfDay(new Date()), []);
  const [agendaDate, setAgendaDate] = useState<Date>(todayStart);
  const [agendaAppointments, setAgendaAppointments] = useState<CareTeamAppointment[]>([]);
  const [loadingAgendaAppointments, setLoadingAgendaAppointments] = useState(false);

  const [selectedAppointment, setSelectedAppointment] = useState<CareTeamAppointment | null>(null);
  const [savingWorkingHour, setSavingWorkingHour] = useState(false);
  const [savingTimeOff, setSavingTimeOff] = useState(false);
  const [savingAppointment, setSavingAppointment] = useState(false);
  const [cancellingAppointment, setCancellingAppointment] = useState(false);

  const patientLookup = useMemo(() => {
    return patients.reduce<Record<string, string>>((acc, patient) => {
      acc[patient.patient_id] = `${patient.first_name || ""} ${patient.last_name || ""}`.trim() ||
        patient.patient_id;
      return acc;
    }, {});
  }, [patients]);

  const calendarEvents: CalendarEvent[] = useMemo(() => {
    return appointments.map((appointment) => ({
      id: appointment.id,
      title: `${patientLookup[appointment.patient_id] || appointment.patient_id}`,
      start: new Date(appointment.start_time),
      end: new Date(appointment.end_time),
      status: appointment.status,
      resource: appointment,
    }));
  }, [appointments, patientLookup]);

  const eventsByDate = useMemo(() => {
    const map = new Map<number, CalendarEvent[]>();
    calendarEvents.forEach((event) => {
      const key = startOfDay(event.start).getTime();
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(event);
    });
    return map;
  }, [calendarEvents]);

  const workingHourRanges = useMemo(() => {
    const ranges: Record<number, Array<{ start: number; end: number }>> = {};
    workingHours
      .filter((hour) => hour.is_active)
      .forEach((hour) => {
        const start = parseTimeToMinutes(hour.start_time);
        const end = parseTimeToMinutes(hour.end_time);
        if (!Number.isFinite(start) || !Number.isFinite(end)) {
          return;
        }
        if (!ranges[hour.weekday]) {
          ranges[hour.weekday] = [];
        }
        ranges[hour.weekday].push({ start, end });
      });
    return ranges;
  }, [workingHours]);

  const weekdaysDisabled = useMemo(() => {
    const disabled = new Set<number>();
    WEEKDAYS.forEach((day) => {
      const ranges = workingHourRanges[day.value];
      if (WEEKEND_DAYS.has(day.value) || !ranges || ranges.length === 0) {
        disabled.add(day.value);
      }
    });
    return disabled;
  }, [workingHourRanges, WEEKDAYS]);

  const isSlotWithinWorkingHours = useCallback(
    (date: Date) => {
      const weekdayIndex = getWeekdayIndexFromDate(date);
      if (WEEKEND_DAYS.has(weekdayIndex)) {
        return false;
      }
      const ranges = workingHourRanges[weekdayIndex];
      if (!ranges || ranges.length === 0) {
        return false;
      }
      const minutes = date.getHours() * 60 + date.getMinutes();
      return ranges.some((range) => minutes >= range.start && minutes < range.end);
    },
    [workingHourRanges]
  );

  const { calendarMinTime, calendarMaxTime } = useMemo(() => {
    const allRanges = Object.values(workingHourRanges).flat();
    const appointmentStarts = calendarEvents.map((event) => getMinutesFromDate(event.start));
    const appointmentEnds = calendarEvents.map((event) => getMinutesFromDate(event.end));

    let minStart = allRanges.length ? Math.min(...allRanges.map((range) => range.start)) : Infinity;
    let maxEnd = allRanges.length ? Math.max(...allRanges.map((range) => range.end)) : -Infinity;

    if (appointmentStarts.length) {
      minStart = Math.min(minStart, Math.min(...appointmentStarts));
    }
    if (appointmentEnds.length) {
      maxEnd = Math.max(maxEnd, Math.max(...appointmentEnds));
    }

    if (!Number.isFinite(minStart) || !Number.isFinite(maxEnd)) {
      return {
        calendarMinTime: minutesToDayDate(0),
        calendarMaxTime: minutesToDayDate(24 * 60 - 1),
      };
    }

    const adjustedMax = Math.max(maxEnd, minStart + 30);

    return {
      calendarMinTime: minutesToDayDate(Math.max(0, Math.min(minStart, 24 * 60 - 30))),
      calendarMaxTime: minutesToDayDate(Math.min(adjustedMax, 24 * 60)),
    };
  }, [workingHourRanges, calendarEvents]);

  const fetchWorkingHours = useCallback(async () => {
    if (!token) return;
    setLoadingWorkingHours(true);
    try {
      const response = await SchedulingService.listWorkingHours(token);
      const results = response.results || [];
      setWorkingHours(results);
      const missing = WEEKDAYS.filter(
        (day) =>
          day.value <= 4 &&
          !results.some((hour) => hour.weekday === day.value && hour.is_active)
      ).map((day) => day.value);
      setMissingWeekdays(missing);
      if (!initialHoursChecked) {
        setShowDefaultHoursDialog(missing.length > 0);
        setInitialHoursChecked(true);
      }
    } catch (error: any) {
      toast.error(error?.message || "Unable to load working hours");
    } finally {
      setLoadingWorkingHours(false);
    }
  }, [token, initialHoursChecked, WEEKDAYS]);

  const fetchTimeOff = useCallback(async () => {
    if (!token) return;
    setLoadingTimeOff(true);
    try {
      const response = await SchedulingService.listTimeOff(token);
      setTimeOffBlocks(response.results || []);
    } catch (error: any) {
      toast.error(error?.message || "Unable to load time off");
    } finally {
      setLoadingTimeOff(false);
    }
  }, [token]);

  const fetchAgendaAppointments = useCallback(async () => {
    if (!token) return;
    setLoadingAgendaAppointments(true);
    try {
      const response = await SchedulingService.listAppointments(token, {
        start: agendaDate.toISOString(),
        end: endOfDay(agendaDate).toISOString(),
      });
      setAgendaAppointments(response.results || []);
    } catch (error: any) {
      console.error("Failed to load appointments for agenda", error);
    } finally {
      setLoadingAgendaAppointments(false);
    }
  }, [token, agendaDate]);

  const fetchAppointments = useCallback(
    async (start: Date, end: Date) => {
      if (!token) return;
      setLoadingAppointments(true);
      try {
        const response = await SchedulingService.listAppointments(token, {
          start: start.toISOString(),
          end: end.toISOString(),
        });
        const results = response.results || [];
        setAppointments(results);
      } catch (error: any) {
        toast.error(error?.message || "Unable to load appointments");
      } finally {
        setLoadingAppointments(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (!token || !user?.organization) return;
    const orgId = user.organization;
    let mounted = true;
    const fetchPatients = async () => {
      setLoadingPatients(true);
      try {
        const url = PatientService.getPatients(orgId, 0, false);
        const response = await fetch(url, {
          headers: {
            authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) {
          throw new Error("Failed to load patients");
        }
        const data = await response.json();
        if (mounted) {
          setPatients(Array.isArray(data) ? data : data?.results || []);
        }
      } catch (error: any) {
        toast.error(error?.message || "Unable to load patients");
      } finally {
        if (mounted) {
          setLoadingPatients(false);
        }
      }
    };
    fetchPatients();
    return () => {
      mounted = false;
    };
  }, [token, user?.organization]);

  useEffect(() => {
    fetchWorkingHours();
    fetchTimeOff();
  }, [fetchWorkingHours, fetchTimeOff]);

  useEffect(() => {
    setCalendarRange(getViewRange(calendarDate, calendarView));
  }, [calendarDate, calendarView]);

  useEffect(() => {
    fetchAppointments(calendarRange.start, calendarRange.end);
  }, [calendarRange, fetchAppointments]);

  useEffect(() => {
    fetchAgendaAppointments();
  }, [fetchAgendaAppointments]);

  // Update agenda date to follow selected day in day view
  useEffect(() => {
    if (calendarView === "day") {
      setAgendaDate(startOfDay(calendarDate));
    }
  }, [calendarView, calendarDate]);

  const handleOpenWorkingHourDialog = (weekday: number) => {
    const existing = workingHours.find((hour) => hour.weekday === weekday);
    if (existing) {
      setWorkingHourDraft(existing);
    } else {
      setWorkingHourDraft({
        weekday,
        start_time: "09:00:00",
        end_time: "17:00:00",
        is_active: true,
      });
    }
    setWorkingHoursListOpen(false);
    setWorkingHourDialogOpen(true);
  };

  const handleSaveWorkingHour = async () => {
    if (!token) return;
    if (workingHourDraft.start_time >= workingHourDraft.end_time) {
      toast.error("Start time must be before end time");
      return;
    }
    setSavingWorkingHour(true);
    try {
      if (workingHourDraft.id) {
        await SchedulingService.updateWorkingHour(token, workingHourDraft.id, {
          start_time: workingHourDraft.start_time,
          end_time: workingHourDraft.end_time,
          is_active: workingHourDraft.is_active,
        });
      } else {
        await SchedulingService.createWorkingHour(token, workingHourDraft);
      }
      toast.success("Working hours saved");
      setWorkingHourDialogOpen(false);
      fetchWorkingHours();
    } catch (error: any) {
      toast.error(error?.message || "Failed to save working hours");
    } finally {
      setSavingWorkingHour(false);
    }
  };

  const handleAcceptDefaultHours = async () => {
    if (!token) return;
    if (missingWeekdays.length === 0) {
      setShowDefaultHoursDialog(false);
      return;
    }
    setSettingDefaultHours(true);
    try {
      for (const weekday of missingWeekdays) {
        const payload = {
          weekday,
          start_time: "09:00:00",
          end_time: "17:00:00",
          is_active: true,
        };
        await SchedulingService.createWorkingHour(token, payload);
      }
      setMissingWeekdays([]);
      toast.success("Default working hours applied");
      setShowDefaultHoursDialog(false);
      fetchWorkingHours();
    } catch (error: any) {
      toast.error(error?.message || "Unable to set default hours");
    } finally {
      setSettingDefaultHours(false);
    }
  };

  const handleOpenTimeOffDialog = (block?: UserTimeOffBlock) => {
    if (block) {
      setTimeOffDraft({
        id: block.id,
        start: new Date(block.start_time),
        end: new Date(block.end_time),
        reason: block.reason,
      });
    } else {
      setTimeOffDraft({
        start: new Date(),
        end: addHours(new Date(), 1),
        reason: "",
      });
    }
    setTimeOffDialogOpen(true);
  };

  const handleOpenAppointmentDialog = () => {
    // Clear patient selection and reset to defaults when opening for a new appointment
    setAppointmentDraft((prev) => {
      // Set default time to next hour with minutes = 00
      const now = new Date();
      const nextHour = new Date(now);
      nextHour.setHours(now.getHours() + 1, 0, 0, 0); // Next hour, 00 minutes
      return {
        patientId: "", // Clear patient selection
        start: nextHour,
        durationMinutes: DEFAULT_DURATION,
        notes: "",
      };
    });
    setAppointmentDialogOpen(true);
  };

  const handleSaveTimeOff = async () => {
    if (!token) return;
    if (timeOffDraft.start >= timeOffDraft.end) {
      toast.error("End time must be after start time");
      return;
    }
    setSavingTimeOff(true);
    const payload = {
      start_time: timeOffDraft.start.toISOString(),
      end_time: timeOffDraft.end.toISOString(),
      reason: timeOffDraft.reason,
    };
    try {
      if (timeOffDraft.id) {
        await SchedulingService.updateTimeOff(token, timeOffDraft.id, payload);
      } else {
        await SchedulingService.createTimeOff(token, payload);
      }
      toast.success("Time off saved");
      setTimeOffDialogOpen(false);
      fetchTimeOff();
    } catch (error: any) {
      toast.error(error?.message || "Failed to save time off");
    } finally {
      setSavingTimeOff(false);
    }
  };

  const handleDeleteTimeOff = async (id?: number) => {
    if (!token || !id) return;
    setSavingTimeOff(true);
    try {
      await SchedulingService.deleteTimeOff(token, id);
      toast.success("Time off removed");
      fetchTimeOff();
    } catch (error: any) {
      toast.error(error?.message || "Unable to delete time off");
    } finally {
      setSavingTimeOff(false);
    }
  };

  const handleSaveAppointment = async () => {
    if (!token) return;
    if (!appointmentDraft.patientId) {
      toast.error("Select a patient");
      return;
    }
    if (appointmentDraft.durationMinutes <= 0) {
      toast.error("Duration must be positive");
      return;
    }
    const end = addMinutes(appointmentDraft.start, appointmentDraft.durationMinutes);
    setSavingAppointment(true);
    try {
      const payload = {
        patient_id: appointmentDraft.patientId,
        start_time: appointmentDraft.start.toISOString(),
        end_time: end.toISOString(),
        notes: appointmentDraft.notes,
      };
      console.log("[MySchedule] Creating appointment with payload:", payload);
      const createdAppointment = await SchedulingService.createAppointment(token, payload);
      console.log("[MySchedule] Appointment created:", createdAppointment);
      if (createdAppointment?.warnings?.length) {
        createdAppointment.warnings.forEach((warning: string) =>
          toast.warn(warning || "Warning returned from scheduling service")
        );
      }
      toast.success("Appointment created");
      setAppointmentDialogOpen(false);
      setAppointmentDraft({
        patientId: "",
        start: new Date(),
        durationMinutes: DEFAULT_DURATION,
        notes: "",
      });
      fetchAppointments(calendarRange.start, calendarRange.end);
      fetchAgendaAppointments();
    } catch (error: any) {
      console.error("[MySchedule] Failed to create appointment:", error);
      const friendly = getReadableError(error) || "Unable to create appointment";
      toast.error(friendly);
    } finally {
      setSavingAppointment(false);
    }
  };

  const handleCancelAppointment = async () => {
    if (!token || !selectedAppointment) return;
    setCancellingAppointment(true);
    try {
      await SchedulingService.cancelAppointment(token, selectedAppointment.id);
      toast.success("Appointment cancelled");
      setSelectedAppointment(null);
      fetchAppointments(calendarRange.start, calendarRange.end);
      fetchAgendaAppointments();
    } catch (error: any) {
      toast.error(error?.message || "Unable to cancel appointment");
    } finally {
      setCancellingAppointment(false);
    }
  };

  const eventPropGetter = useCallback(
    (event: CalendarEvent) => {
      if (calendarView === "month") {
        return { style: { display: "none" } };
      }
      const cancelled = event.status === "cancelled";
      const backgroundColor = cancelled
        ? theme.palette.warning.light
        : theme.palette.primary.main;
      return {
        style: {
          backgroundColor,
          color: "white",
          borderRadius: 8,
          opacity: cancelled ? 0.6 : 0.95,
          border: "none",
        },
      };
    },
    [theme.palette, calendarView]
  );

  if (!user) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  if (!roleCanManageSchedule(user)) {
    return (
      <Box p={4}>
        <Typography variant="h5" gutterBottom>
          Schedule access unavailable
        </Typography>
        <Typography color="text.secondary">
          You need elevated patient-management permissions to manage the care-team schedule.
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={3}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={2} mb={3}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {t("my_schedule")}
            </Typography>
            <Typography color="text.secondary">
              {t("schedule_subtitle")}
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<EventBusyIcon />}
              onClick={() => handleOpenTimeOffDialog()}
            >
              {t("block_time_off")}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAppointmentDialog}
            >
              {t("create_appointment")}
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={3} sx={{ fontFamily: `"Inter", "Roboto", "Helvetica", sans-serif` }}>
          <Grid item xs={12} md={8}>
            <Card sx={{ height: "100%", fontFamily: `"Inter", "Roboto", "Helvetica", sans-serif` }}>
              <CardHeader
                title={t("schedule")}
                action={
                  <Stack direction="row" spacing={1} alignItems="center">
                    {loadingAppointments && (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CircularProgress size={20} />
                        <Typography variant="body2">{t("loading")}</Typography>
                      </Stack>
                    )}
                    <Button size="small" onClick={() => setTimeOffListOpen(true)}>
                      {t("time_off")}
                    </Button>
                    <Button size="small" onClick={() => setWorkingHoursListOpen(true)}>
                      {t("working_hours")}
                    </Button>
                  </Stack>
                }
              />
              <Divider />
                <CardContent sx={{ height: "72vh", fontFamily: `"Inter", "Roboto", "Helvetica", sans-serif` }}>
                <Calendar
                  localizer={localizer}
                  culture={i18n.language}
                  messages={getCalendarMessages(t)}
                  events={calendarEvents}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: "100%" }}
                  views={AVAILABLE_VIEWS}
                  view={calendarView}
                  date={calendarDate}
                  onView={(view: CalendarView) => setCalendarView(view)}
                  onNavigate={(date: Date) => setCalendarDate(date)}
                  eventPropGetter={eventPropGetter}
                  min={calendarMinTime}
                  max={calendarMaxTime}
                  {...(['week', 'day'].includes(calendarView) && { formats: { eventTimeRangeFormat: () => '' } })}
                  components={{
                    event: ({ event }: { event: CalendarEvent }) => (
                      <Typography variant="caption" fontWeight={600} color="white">
                        {patientLookup[event.resource.patient_id] || event.resource.patient_id}
                      </Typography>
                    ),
                    month: {
                      dateHeader: ({ label, date }: { label: string; date: Date }) => {
                        const key = startOfDay(date).getTime();
                        const dayEvents = eventsByDate.get(key) || [];
                        const count = dayEvents.length;
                        const tooltip =
                          count > 0 ? (
                            <Stack spacing={0.5}>
                              {dayEvents.map((event) => (
                                <Typography key={event.id} variant="caption" display="block">
                                  {format(event.start, "p")} ·{" "}
                                  {patientLookup[event.resource.patient_id] || event.resource.patient_id}
                                </Typography>
                              ))}
                            </Stack>
                          ) : (
                            ""
                          );
                        return (
                          <Tooltip
                            title={tooltip}
                            disableInteractive={count === 0}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  backgroundColor: theme.palette.grey[200],
                                  color: theme.palette.text.primary,
                                  boxShadow: theme.shadows[3],
                                },
                              },
                            }}
                          >
                            <Box
                              onClick={() => setAgendaDate(startOfDay(date))}
                              sx={{
                                cursor: "pointer",
                                minHeight: 90,
                                pt: 0.5,
                                pl: 0.5,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                              }}
                            >
                              <Box width="100%" display="flex" justifyContent="flex-start">
                                <Typography
                                  variant="caption"
                                  fontWeight="bold"
                                  sx={{ fontFamily: `"Inter", "Roboto", "Helvetica", sans-serif` }}
                                >
                                  {label}
                                </Typography>
                              </Box>
                              {count > 0 && (
                                <Box
                                  width="100%"
                                  display="flex"
                                  justifyContent="center"
                                  mt={1.5}
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      width: 56,
                                      height: 56,
                                      borderRadius: "50%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "1rem",
                                      fontWeight: 700,
                                      backgroundColor:
                                        theme.palette.mode === "dark"
                                          ? "rgba(255,255,255,0.25)"
                                          : theme.palette.primary.main,
                                      color: theme.palette.getContrastText(
                                        theme.palette.primary.main
                                      ),
                                    }}
                                  >
                                    {count}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </Tooltip>
                        );
                      },
                      event: () => null,
                    },
                  }}
                  dayPropGetter={(date: Date) => {
                    const weekdayIndex = getWeekdayIndexFromDate(date);
                    const styles: CSSProperties = {};
                    const nonWorkingBg =
                      theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "#f5f5f5";
                    if (weekdaysDisabled.has(weekdayIndex)) {
                      styles.backgroundColor = nonWorkingBg;
                    }
                    const dayStart = startOfDay(date);
                    if (calendarView === "month" && dayStart < todayStart) {
                      styles.backgroundColor =
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : theme.palette.grey[200];
                    }
                    return Object.keys(styles).length ? { style: styles } : {};
                  }}
                  slotPropGetter={(date: Date) => {
                    const styles: CSSProperties = {};
                    
                    // Add thick border for today's time slots in week view
                    if (calendarView === "week" && startOfDay(date).getTime() === todayStart.getTime()) {
                      styles.borderLeft = `3px solid ${theme.palette.primary.main}`;
                      styles.borderRight = `3px solid ${theme.palette.primary.main}`;
                    }
                    
                    if (isSlotWithinWorkingHours(date)) {
                      return Object.keys(styles).length ? { style: styles } : {};
                    }
                    return {
                      style: {
                        ...styles,
                        backgroundColor:
                          theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "#f5f5f5",
                      },
                    };
                  }}
                  onSelectEvent={(event: CalendarEvent) => setSelectedAppointment(event.resource)}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack spacing={3} sx={{ fontFamily: `"Inter", "Roboto", "Helvetica", sans-serif` }}>
              <Card sx={{ fontFamily: `"Inter", "Roboto", "Helvetica", sans-serif` }}>
                <CardHeader title={`${t("agenda_for")} ${format(agendaDate, "MMM d, yyyy")}`} />
                <CardContent>
                  {loadingAgendaAppointments ? (
                    <Box py={2} display="flex" justifyContent="center">
                      <CircularProgress size={20} />
                    </Box>
                  ) : agendaAppointments.length === 0 ? (
                    <Typography color="text.secondary">{t("no_appointments_scheduled")}</Typography>
                  ) : (
                    <Stack spacing={1}>
                      {agendaAppointments.map((appointment) => {
                        const isPast =
                          new Date(appointment.end_time).getTime() <
                          new Date().getTime();
                        return (
                        <Box
                          key={appointment.id}
                          onClick={() => navigate(`/patient/${appointment.patient_id}`)}
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            p: 1,
                            cursor: "pointer",
                            opacity: isPast ? 0.6 : 1,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.08)"
                                  : theme.palette.grey[100],
                            },
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight="bold">
                            {patientLookup[appointment.patient_id] || appointment.patient_id}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {format(new Date(appointment.start_time), "p")} -{" "}
                            {format(new Date(appointment.end_time), "p")}
                          </Typography>
                          {appointment.notes && (
                            <Typography variant="body2" color="text.secondary">
                              {appointment.notes}
                            </Typography>
                         )}
                       </Box>
                        );
                      })}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>

        {/* Default hours prompt */}
        <Dialog
          open={showDefaultHoursDialog}
          onClose={() => setShowDefaultHoursDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t("use_default_working_hours")}</DialogTitle>
          <DialogContent dividers>
            <Typography>
              {t("default_hours_prompt")}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDefaultHoursDialog(false)}>{t("no")}</Button>
            <Button
              variant="contained"
              onClick={handleAcceptDefaultHours}
              disabled={settingDefaultHours}
            >
              {settingDefaultHours ? <CircularProgress size={20} /> : t("yes")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Time off list dialog */}
        <Dialog
          open={timeOffListOpen}
          onClose={() => setTimeOffListOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t("time_off")}</DialogTitle>
          <DialogContent dividers>
            {loadingTimeOff ? (
              <Box py={4} display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            ) : timeOffBlocks.length === 0 ? (
              <Typography color="text.secondary">{t("no_upcoming_blocks")}</Typography>
            ) : (
              <Stack spacing={1}>
                {timeOffBlocks
                  .slice()
                  .sort(
                    (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
                  )
                  .map((block) => (
                    <Box
                      key={block.id}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2">
                          {format(new Date(block.start_time), "MMM d, HH:mm")} -{" "}
                          {format(new Date(block.end_time), "MMM d, HH:mm")}
                        </Typography>
                        {block.reason && (
                          <Typography variant="body2" color="text.secondary">
                            {block.reason}
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title={t("edit")}>
                          <IconButton size="small" onClick={() => handleOpenTimeOffDialog(block)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("delete")}>
                          <IconButton size="small" onClick={() => handleDeleteTimeOff(block.id)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  ))}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setTimeOffListOpen(false);
                handleOpenTimeOffDialog();
              }}
            >
              {t("add_block")}
            </Button>
            <Button onClick={() => setTimeOffListOpen(false)}>{t("close")}</Button>
          </DialogActions>
        </Dialog>

        {/* Working hours list dialog */}
        <Dialog
          open={workingHoursListOpen}
          onClose={() => setWorkingHoursListOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t("working_hours")}</DialogTitle>
          <DialogContent dividers>
            {loadingWorkingHours ? (
              <Box py={4} display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={1}>
                {WEEKDAYS.map((day) => {
                  const entry = workingHours.find((hour) => hour.weekday === day.value);
                  return (
                    <Box
                      key={day.value}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : theme.palette.grey[50],
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2">{day.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {entry
                            ? entry.is_active
                              ? `${entry.start_time.substring(0, 5)} - ${entry.end_time.substring(0, 5)}`
                              : t("not_working")
                            : WEEKEND_DAYS.has(day.value)
                            ? t("not_working")
                            : t("not_set")}
                        </Typography>
                      </Box>
                      <Tooltip title={entry ? t("edit_hours") : t("add_hours")}>
                        <IconButton size="small" onClick={() => handleOpenWorkingHourDialog(day.value)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setWorkingHoursListOpen(false)}>{t("close")}</Button>
          </DialogActions>
        </Dialog>

        {/* Working hours dialog */}
        <Dialog open={workingHourDialogOpen} onClose={() => setWorkingHourDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{t("set_working_hours")}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                label={t("weekday")}
                value={
                  WEEKDAYS.find((day) => day.value === workingHourDraft.weekday)?.label || ""
                }
                InputProps={{ readOnly: true }}
              />
              <TimePicker
                label={t("start")}
                value={timeStringToDate(workingHourDraft.start_time)}
                onChange={(date: Date | null) =>
                  setWorkingHourDraft((prev) => ({
                    ...prev,
                    start_time: formatTime(date),
                  }))
                }
                renderInput={(params) => <TextField {...params} />}
              />
              <TimePicker
                label={t("end")}
                value={timeStringToDate(workingHourDraft.end_time)}
                onChange={(date: Date | null) =>
                  setWorkingHourDraft((prev) => ({
                    ...prev,
                    end_time: formatTime(date),
                  }))
                }
                renderInput={(params) => <TextField {...params} />}
              />
              <Stack direction="row" spacing={1}>
                <Chip
                  color={workingHourDraft.is_active ? "success" : "default"}
                  label={workingHourDraft.is_active ? t("active") : t("inactive")}
                  onClick={() =>
                    setWorkingHourDraft((prev) => ({
                      ...prev,
                      is_active: !prev.is_active,
                    }))
                  }
                />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setWorkingHourDialogOpen(false)}>{t("cancel")}</Button>
            <Button onClick={handleSaveWorkingHour} variant="contained" disabled={savingWorkingHour}>
              {savingWorkingHour ? <CircularProgress size={20} /> : t("save")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Time off dialog */}
        <Dialog open={timeOffDialogOpen} onClose={() => setTimeOffDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{timeOffDraft.id ? t("edit_time_off") : t("add_time_off")}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <DateTimePicker
                label={t("start")}
                value={timeOffDraft.start}
                onChange={(value: Date | null) =>
                  value && setTimeOffDraft((prev) => ({ ...prev, start: value }))
                }
                renderInput={(params) => <TextField {...params} />}
              />
              <DateTimePicker
                label={t("end")}
                value={timeOffDraft.end}
                onChange={(value: Date | null) =>
                  value && setTimeOffDraft((prev) => ({ ...prev, end: value }))
                }
                renderInput={(params) => <TextField {...params} />}
              />
              <TextField
                label={t("reason")}
                value={timeOffDraft.reason || ""}
                onChange={(event) =>
                  setTimeOffDraft((prev) => ({ ...prev, reason: event.target.value }))
                }
                multiline
                minRows={2}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTimeOffDialogOpen(false)}>{t("cancel")}</Button>
            <Button onClick={handleSaveTimeOff} variant="contained" disabled={savingTimeOff}>
              {savingTimeOff ? <CircularProgress size={20} /> : t("save")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Appointment dialog */}
        <Dialog
          open={appointmentDialogOpen}
          onClose={() => setAppointmentDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t("schedule_appointment")}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Autocomplete
                loading={loadingPatients}
                options={patients}
                getOptionLabel={(option) =>
                  `${option.first_name || ""} ${option.last_name || ""}`.trim() ||
                  option.patient_id
                }
                value={
                  patients.find((patient) => patient.patient_id === appointmentDraft.patientId) ||
                  null
                }
                onChange={(_, value) =>
                  setAppointmentDraft((prev) => ({
                    ...prev,
                    patientId: value?.patient_id || "",
                  }))
                }
                renderInput={(params) => <TextField {...params} label={t("patient")} />}
              />
              <DatePicker
                label={t("date")}
                value={appointmentDraft.start}
                onChange={(value) =>
                  value &&
                  setAppointmentDraft((prev) => ({
                    ...prev,
                    start: new Date(
                      value.getFullYear(),
                      value.getMonth(),
                      value.getDate(),
                      prev.start.getHours(),
                      prev.start.getMinutes(),
                      0,
                      0
                    ),
                  }))
                }
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="hour-select-label">{t("hour")}</InputLabel>
                  <Select
                    labelId="hour-select-label"
                    label={t("hour")}
                    value={appointmentDraft.start.getHours()}
                    onChange={(event) =>
                      setAppointmentDraft((prev) => {
                        const newDate = new Date(prev.start);
                        newDate.setHours(Number(event.target.value));
                        return { ...prev, start: newDate };
                      })
                    }
                  >
                    {Array.from({ length: 24 }, (_, hour) => (
                      <MenuItem key={hour} value={hour}>
                        {hour.toString().padStart(2, "0")}:00
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel id="minute-select-label">{t("minutes")}</InputLabel>
                  <Select
                    labelId="minute-select-label"
                    label={t("minutes")}
                    value={appointmentDraft.start.getMinutes()}
                    onChange={(event) =>
                      setAppointmentDraft((prev) => {
                        const newDate = new Date(prev.start);
                        newDate.setMinutes(Number(event.target.value));
                        return { ...prev, start: newDate };
                      })
                    }
                  >
                    {[0, 15, 30, 45].map((minute) => (
                      <MenuItem key={minute} value={minute}>
                        {minute.toString().padStart(2, "0")}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <TextField
                label={t("duration_minutes")}
                type="number"
                value={appointmentDraft.durationMinutes}
                onChange={(event) =>
                  setAppointmentDraft((prev) => ({
                    ...prev,
                    durationMinutes: Number(event.target.value),
                  }))
                }
                inputProps={{ min: 5, step: 5 }}
              />
              <TextField
                label={t("notes")}
                multiline
                minRows={2}
                value={appointmentDraft.notes}
                onChange={(event) =>
                  setAppointmentDraft((prev) => ({ ...prev, notes: event.target.value }))
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              // Clear patient selection when dialog is cancelled
              setAppointmentDraft((prev) => ({ ...prev, patientId: "" }));
              setAppointmentDialogOpen(false);
            }}>{t("cancel")}</Button>
            <Button onClick={handleSaveAppointment} variant="contained" disabled={savingAppointment || !appointmentDraft.patientId}>
              {savingAppointment ? <CircularProgress size={20} /> : t("create")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Appointment details */}
        <Dialog
          open={Boolean(selectedAppointment)}
          onClose={() => setSelectedAppointment(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t("appointment_details")}</DialogTitle>
          {selectedAppointment && (
            <>
              <DialogContent dividers>
                <Stack spacing={1.5}>
                  <Typography variant="h6">
                    {patientLookup[selectedAppointment.patient_id] || selectedAppointment.patient_id}
                  </Typography>
                  <Chip
                    label={selectedAppointment.status}
                    color={selectedAppointment.status === "cancelled" ? "warning" : "success"}
                    size="small"
                  />
                  <Typography>
                    {format(new Date(selectedAppointment.start_time), "PPpp")} -{" "}
                    {format(new Date(selectedAppointment.end_time), "PPpp")}
                  </Typography>
                  {selectedAppointment.notes && (
                    <Typography color="text.secondary">{selectedAppointment.notes}</Typography>
                  )}
                  {selectedAppointment.user_local_start && (
                    <Typography variant="body2" color="text.secondary">
                      {t("user_local")}: {selectedAppointment.user_local_start} -{" "}
                      {selectedAppointment.user_local_end}
                    </Typography>
                  )}
                  {selectedAppointment.patient_local_start && (
                    <Typography variant="body2" color="text.secondary">
                      {t("patient_local")}: {selectedAppointment.patient_local_start} -{" "}
                      {selectedAppointment.patient_local_end}
                    </Typography>
                  )}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSelectedAppointment(null)}>{t("close")}</Button>
                <Button
                  color="error"
                  variant="contained"
                  onClick={handleCancelAppointment}
                  disabled={selectedAppointment.status === "cancelled" || cancellingAppointment}
                >
                  {cancellingAppointment ? <CircularProgress size={20} /> : t("cancel_appointment")}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default MySchedule;
